import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { companyHubTextColorMessages } from '@atlaskit/editor-common/messages';
import { useEditorToolbar } from '@atlaskit/editor-common/toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Popup } from '@atlaskit/editor-common/ui';
import {
	OutsideClickTargetRefContext,
	withReactEditorViewOuterListeners,
} from '@atlaskit/editor-common/ui-react';
import {
	hexToEditorTextBackgroundPaletteColor,
	hexToEditorTextPaletteColor,
} from '@atlaskit/editor-palette';
import type { HighlightPlugin } from '@atlaskit/editor-plugin-highlight';
import type { TextColorPlugin } from '@atlaskit/editor-plugin-text-color';
import { akEditorFloatingOverlapPanelZIndex } from '@atlaskit/editor-shared-styles';
import {
	TextColorIcon,
	ToolbarButton,
	ToolbarColorSwatch,
	ToolbarTooltip,
	useToolbarUI,
} from '@atlaskit/editor-toolbar';
import type { ToolbarComponentTypes } from '@atlaskit/editor-toolbar-model';
import { Stack } from '@atlaskit/primitives/compiled';
import { getTokenValue, token } from '@atlaskit/tokens';
import { type IconColor } from '@atlaskit/tokens/css-type-schema';
import { ColorPickerDropdown, createBuiltInColorMap } from '@atlassian/color-picker';

import type { CompanyHubTextColorPlugin } from '../../companyHubTextColorPluginType';
import { changeColor as changeColorWithAnalytics } from '../../editor-commands/change-color';

import { HighlightColorSection } from './HighlightColorSection';
import { RemoveColorSection } from './RemoveColorSection';

interface CompanyHubTextColorButtonProps {
	api?: ExtractInjectionAPI<CompanyHubTextColorPlugin>;
	parents: ToolbarComponentTypes;
}

const popupComponentStyles = cssMap({
	popupWrapper: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.overlay'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
});

const getHighlightColorIcon = (highlightColor: string | null | undefined) => {
	if (highlightColor) {
		return hexToEditorTextBackgroundPaletteColor(highlightColor);
	}
	return undefined;
};

const PopupWithListeners = withReactEditorViewOuterListeners(Popup);

export const CompanyHubTextColorButton = ({ api, parents }: CompanyHubTextColorButtonProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const { popupsMountPoint, popupsBoundariesElement, popupsScrollableElement } = useToolbarUI();
	const { editorView } = useEditorToolbar();
	const [isOpen, setIsOpen] = useState(false);

	const {
		color,
		defaultColor,
		disabled: textColorDisabled,
		highlightColor,
	} = useSharedPluginStateWithSelector(api, ['companyHubTextColor', 'highlight'], (states) => ({
		color: states.companyHubTextColorState?.color,
		defaultColor: states.companyHubTextColorState?.defaultColor,
		disabled: states.companyHubTextColorState?.disabled,
		highlightColor: states.highlightState?.activeColor,
	}));

	const blackWhiteColorMap = useMemo(
		() =>
			createBuiltInColorMap({
				knownColors: [getTokenValue('color.text.inverse'), getTokenValue('color.text')],
			}),
		[],
	);

	const getIconColor = useCallback((): string => {
		if (!color || color === defaultColor) {
			return getTokenValue('color.text');
		}
		// find a matching editor text palette color for the hex color
		// if none is found, return the hex color itself
		return hexToEditorTextPaletteColor(color) || color || getTokenValue('color.text');
	}, [color, defaultColor]);

	const handleColorChange = useCallback(
		(color: string) => {
			if (color && !textColorDisabled && editorView?.state && editorView?.dispatch) {
				changeColorWithAnalytics(color, api?.analytics?.actions)(
					editorView.state,
					editorView.dispatch,
				);
				editorView.focus();
			}
			setIsOpen(false);
			return false;
		},
		[textColorDisabled, editorView, api?.analytics?.actions],
	);

	const handleInternalClick = useCallback((evt: React.MouseEvent<HTMLDivElement>) => {
		// Stop propagation of clicks inside the popup to prevent the document click listener from triggering
		// Without this, clicking on RGB/HEX/HSL dropdown would close the popup immediately
		evt.stopPropagation();
	}, []);

	const handleInternalKeyDown = useCallback((evt: React.KeyboardEvent<HTMLDivElement>) => {
		// Stop propagation of keyboard events inside the popup to prevent the document listener from triggering
		// Support standard keyboard interactions for interactive elements within the popup
		if (evt.key === 'Enter' || evt.key === ' ') {
			evt.stopPropagation();
		}
	}, []);

	const closeMenu = useCallback(() => {
		setIsOpen(false);
	}, [setIsOpen]);

	const handleClick = useCallback(() => {
		setIsOpen(!isOpen);
	}, [setIsOpen, isOpen]);

	if (!api?.companyHubTextColor || color === undefined || defaultColor === undefined) {
		return null;
	}

	const iconColor = getIconColor();
	const isDisabled = Boolean(textColorDisabled);

	return (
		<>
			{isOpen && buttonRef.current && (
				<PopupWithListeners
					target={buttonRef.current}
					zIndex={akEditorFloatingOverlapPanelZIndex}
					mountTo={popupsMountPoint}
					boundariesElement={popupsBoundariesElement}
					scrollableElement={popupsScrollableElement}
					alignY="top"
					alignX="left"
					handleClickOutside={closeMenu}
					handleEscapeKeydown={closeMenu}
					allowOutOfBounds
					fitHeight={350}
					fitWidth={350}
					preventOverflow={true}
					stick={false}
					offset={[0, 10]}
					focusTrap
				>
					<OutsideClickTargetRefContext.Consumer>
						{(setOutsideClickTargetRef) => (
							<div
								role="none"
								data-test-id={'company-hub-text-color-picker'}
								onClick={handleInternalClick}
								onKeyDown={handleInternalKeyDown}
								ref={setOutsideClickTargetRef}
							>
								<Stack xcss={popupComponentStyles.popupWrapper}>
									<ColorPickerDropdown
										analyticsSource="companyHubToolbarTextColor"
										builtInColorMap={blackWhiteColorMap}
										onChange={handleColorChange}
										value={color || defaultColor}
										pickerTitle={companyHubTextColorMessages.colorPickerTitle}
										showCustomPalette
										enableBorderLessStyle={false}
										isOpen={true}
										update={() => Promise.resolve(null)}
										setInitialFocusRef={() => null}
									/>
									<HighlightColorSection
										api={api as ExtractInjectionAPI<HighlightPlugin>}
										parents={parents}
									/>
									<RemoveColorSection
										api={api as ExtractInjectionAPI<TextColorPlugin>}
										parents={parents}
									/>
								</Stack>
							</div>
						)}
					</OutsideClickTargetRefContext.Consumer>
				</PopupWithListeners>
			)}
			<ToolbarTooltip content={formatMessage(companyHubTextColorMessages.colorPickerTitle)}>
				<ToolbarButton
					testId="company-hub-text-color-toolbar-button"
					iconBefore={
						<ToolbarColorSwatch highlightColor={getHighlightColorIcon(highlightColor)}>
							<TextColorIcon
								label={formatMessage(companyHubTextColorMessages.colorPickerTitle)}
								iconColor={iconColor as IconColor}
								size={'small'}
								isDisabled={isDisabled}
								spacing={'compact'}
							/>
						</ToolbarColorSwatch>
					}
					ref={buttonRef}
					onClick={handleClick}
					isSelected={isOpen}
					isDisabled={isDisabled}
				/>
			</ToolbarTooltip>
		</>
	);
};
